/**
 * Per-lesson "Convert lesson to PPTX" panel rendered inside the LessonDialog
 * (admin / SME / instructor curriculum editor).
 *
 * Mirrors the LessonVideoRenderPanel UX:
 *   - If the lesson already has a generated deck, show the download CTA + meta.
 *   - Otherwise show a "Generate PPTX" CTA that kicks off the background job.
 *   - In-flight jobs share progress with the global AiJobsTray so the user
 *     can close the dialog and pick up the download later.
 *
 * The same .pptx is later shown to students under the lesson body so they
 * can download it directly from the Learn page.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
    Presentation, Download, Loader2, Trash2, RefreshCw, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = {
    queued: "Queued for AI…",
    planning: "AI is planning slides…",
    rendering: "Building PowerPoint deck…",
    uploading: "Uploading deck…",
    done: "Done — PPTX ready",
    failed: "Failed",
};

function formatBytes(n) {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function LessonPptxPanel({
    lessonId,
    lessonTitle,
    chapterTitle,
    pptxUrl,
    pptxFilename,
    pptxSizeBytes,
    pptxSlideCount,
    onChanged,
}) {
    const { trackJob, jobs: trayJobs } = useAiJobs();
    const [jobId, setJobId] = useState(null);
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const [localPptxUrl, setLocalPptxUrl] = useState(null);
    const pollRef = useRef(null);

    const fullPptxUrl = pptxUrl || localPptxUrl || null;

    useEffect(() => {
        if (pptxUrl && localPptxUrl && pptxUrl === localPptxUrl) {
            setLocalPptxUrl(null);
        }
    }, [pptxUrl, localPptxUrl]);

    useEffect(() => {
        if (!jobId) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/lesson-pptx-jobs/${jobId}`);
                if (cancelled) return;
                setJob(data);
                if (data.status === "done") {
                    setBusy(false);
                    setJobId(null);
                    toast.success("Lesson deck ready");
                    if (data.pptx_url) setLocalPptxUrl(data.pptx_url);
                    onChanged?.(data.pptx_url);
                } else if (data.status === "failed") {
                    setBusy(false);
                    setJobId(null);
                    toast.error(`PPTX failed: ${data.error || "unknown error"}`);
                }
            } catch (e) {
                if (!cancelled) {
                    setBusy(false);
                    setJobId(null);
                }
            }
        };
        tick();
        pollRef.current = window.setInterval(tick, 2200);
        return () => {
            cancelled = true;
            if (pollRef.current) window.clearInterval(pollRef.current);
        };
    }, [jobId, onChanged]);

    // Resume polling if a job for this lesson is still running in the tray.
    useEffect(() => {
        if (!lessonId || busy || jobId) return;
        const trayJob = Object.values(trayJobs || {}).find(
            (j) => j.kind === "pptx" && j.lessonId === lessonId
                && (j.status === "running" || j.status === "queued"),
        );
        if (trayJob) {
            setJobId(trayJob.jobId);
            setJob({
                status: trayJob.status,
                stage: trayJob.stage || "queued",
            });
            setBusy(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId, trayJobs]);

    const startGenerate = async () => {
        if (!lessonId) {
            toast.error("Save the lesson first, then generate the deck.");
            return;
        }
        setBusy(true);
        setJob(null);
        try {
            const { data } = await api.post(`/lessons/${lessonId}/pptx/generate`);
            setJobId(data.job_id);
            setJob({ status: "queued", stage: "queued" });
            trackJob({
                kind: "pptx",
                jobId: data.job_id,
                lessonId,
                lessonTitle: data.lesson_title || lessonTitle || "Lesson",
                chapterTitle: data.chapter_title || chapterTitle || "Chapter",
                total: 10,
                onDone: (jobData) => {
                    try {
                        window.dispatchEvent(new CustomEvent("cco:lesson-pptx-generated", {
                            detail: { lessonId, pptxUrl: jobData?.pptx_url },
                        }));
                    } catch { /* no-op */ }
                },
            });
        } catch (e) {
            setBusy(false);
            toast.error(e?.response?.data?.detail || "Could not start PPTX generation.");
        }
    };

    const removeDeck = async () => {
        if (!lessonId) return;
        try {
            await api.delete(`/lessons/${lessonId}/pptx`);
            toast.success("Deck removed");
            setLocalPptxUrl(null);
            onChanged?.("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not remove deck.");
        }
    };

    if (!lessonId) {
        return (
            <div
                className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
                data-testid="lesson-pptx-panel-disabled"
            >
                Save the lesson first to enable PowerPoint generation.
            </div>
        );
    }

    // 1) In-flight render
    if (busy || jobId) {
        const stage = job?.stage || "queued";
        const failed = job?.status === "failed";
        return (
            <div
                className="rounded-md border border-amber-300/60 bg-amber-50/60 px-4 py-3 space-y-2"
                data-testid="lesson-pptx-progress"
            >
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    {failed ? (
                        <AlertCircle className="h-4 w-4" />
                    ) : (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {failed ? "Deck generation failed" : STAGE_LABELS[stage] || "Working…"}
                </div>
                {!failed ? (
                    <Progress value={stage === "uploading" ? 90 : stage === "rendering" ? 70 : stage === "planning" ? 35 : 8} />
                ) : null}
                <p className="text-xs text-amber-900/70">
                    You can close this dialog — progress lives in the AI jobs tray (bottom-right).
                </p>
                {failed ? (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setJob(null);
                            setJobId(null);
                            setBusy(false);
                        }}
                        data-testid="lesson-pptx-reset-btn"
                    >
                        Try again
                    </Button>
                ) : null}
            </div>
        );
    }

    // 2) Already generated → download CTA
    if (fullPptxUrl) {
        return (
            <div
                className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 space-y-2"
                data-testid="lesson-pptx-ready"
            >
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CheckCircle2 className="h-4 w-4" />
                    PowerPoint deck attached
                    {pptxSlideCount ? (
                        <span className="font-normal text-muted-foreground">
                            · {pptxSlideCount} slides
                            {pptxSizeBytes ? ` · ${formatBytes(pptxSizeBytes)}` : ""}
                        </span>
                    ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <a href={fullPptxUrl} target="_blank" rel="noopener noreferrer" download={pptxFilename || true}>
                        <Button size="sm" className="gap-1.5" data-testid="lesson-pptx-download-btn">
                            <Download className="h-4 w-4" />
                            Download .pptx
                        </Button>
                    </a>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={startGenerate}
                        className="gap-1.5"
                        data-testid="lesson-pptx-regenerate-btn"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Regenerate
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={removeDeck}
                        className="gap-1.5 text-destructive hover:bg-destructive/10"
                        data-testid="lesson-pptx-remove-btn"
                    >
                        <Trash2 className="h-4 w-4" />
                        Remove
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    Students will see this download under the lesson body on the Learn page.
                </p>
            </div>
        );
    }

    // 3) Not yet generated → CTA
    return (
        <div
            className="rounded-md border border-border bg-card px-4 py-3 space-y-2"
            data-testid="lesson-pptx-empty"
        >
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Presentation className="h-4 w-4 text-primary" />
                Convert this lesson to PowerPoint
            </div>
            <p className="text-xs text-muted-foreground">
                AI plans 8-12 branded Cambridge slides with speaker notes,
                renders a downloadable .pptx, and attaches it to this lesson
                so students can grab a copy.
            </p>
            <Button
                size="sm"
                onClick={startGenerate}
                className="gap-1.5"
                data-testid="lesson-pptx-generate-btn"
            >
                <Presentation className="h-4 w-4" />
                Generate PPTX
            </Button>
        </div>
    );
}
